import { useEffect, useState, useCallback, useRef, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { App as CapApp } from '@capacitor/app';
const TelecomPlugin = registerPlugin('TelecomPlugin');
import axios from "axios"; // Keep for WebRTC signaling
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Bell, Download, QrCode, Phone, PhoneOff, PhoneIncoming, Settings, History, Shield, Clock, User, LogOut, RefreshCw, Copy, Send, Video, VideoOff, Mic, MicOff, X, Check, Loader2, Ban, Trash2, CheckCircle2, XCircle } from "lucide-react";
import appwriteService, { verifyAppwriteSetup } from './services/appwriteService';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const VisitorCallPage = lazy(() => import('./pages/VisitorCallPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const IncomingRingPage = lazy(() => import('./pages/IncomingRingPage'));
const AppAuthBridge = lazy(() => import('./pages/AppAuthBridge'));


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = "https://auth.emergentagent.com";

// Auth Context
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await appwriteService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = async () => {
    try {
      await appwriteService.logout();
      setUser(null);
      window.location.href = "/";
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  return { user, loading, checkAuth, logout, setUser, setLoading };
};

function useIncomingCallListener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    // 1. Listen for the push data payload
    const listener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      const data = notification.data;
      
      let type = null;
      let visitorName = 'Visitor';
      let roomId = null;

      if (data && data.info) {
        try {
          const info = JSON.parse(data.info);
          type = info.type;
          visitorName = info.callerInfo?.callerId || 'Visitor';
          roomId = info.videoSDKInfo?.meetingId;
        } catch (e) {
          console.error("Failed to parse info", e);
        }
      } else if (data) {
        type = data.type;
        visitorName = data.visitorName || data.visitor_name || 'Visitor';
        roomId = data.roomId || data.room_id;
      }

      if (type === 'INCOMING_CALL' || type === 'doorbell_ring' || type === 'CALL_INITIATED') {
        if (roomId) {
          navigate(`/incoming-ring`, { 
            state: { 
              visitorName: visitorName,
              roomId: roomId
            }
          });
        }
      }
    });

    return () => {
      listener.then(l => l.remove());
    };
  }, [navigate]);
}

// useAppUrlOpenListener removed to be inline in App()

// Main App
function App() {
  const { user, loading, checkAuth, logout, setUser, setLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useIncomingCallListener();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let ringingListener, answeredListener, rejectedListener;

    const setupTelecomListeners = async () => {
      try {
        ringingListener = await TelecomPlugin.addListener('callRinging', (data) => {
          console.log("Global: Native call ringing event received:", data);
          if (data && data.roomId) {
            navigate(`/incoming-ring`, { 
              state: { 
                visitorName: data.visitorName || 'Visitor',
                roomId: data.roomId
              }
            });
          }
        });

        answeredListener = await TelecomPlugin.addListener('callAnswered', (data) => {
          console.log("Global: Native call answered event received:", data);
          if (data && data.roomId) {
            navigate(`/`, {
              state: {
                autoAcceptCallId: data.roomId,
                visitorName: data.visitorName || 'Visitor'
              }
            });
          }
        });

        rejectedListener = await TelecomPlugin.addListener('callRejected', (data) => {
          console.log("Global: Native call rejected event received:", data);
          if (data && data.roomId) {
            navigate(`/`, {
              state: {
                autoRejectCallId: data.roomId
              }
            });
          }
        });

        // Check for pending actions on startup (cold start)
        const result = await TelecomPlugin.getPendingCallAction();
        console.log("Global: Pending call action check on cold start:", result);
        if (result) {
          if (result.action === "ringing" && result.roomId) {
            console.log("Global: Cold start pending ringing found for room:", result.roomId);
            navigate(`/incoming-ring`, { 
              state: { 
                visitorName: result.visitorName || 'Visitor',
                roomId: result.roomId
              }
            });
          } else if (result.action === "answered" && result.roomId) {
            console.log("Global: Cold start pending answered found for room:", result.roomId);
            navigate(`/`, {
              state: {
                autoAcceptCallId: result.roomId,
                visitorName: result.visitorName || 'Visitor'
              }
            });
          } else if (result.action === "rejected" && result.roomId) {
            console.log("Global: Cold start pending rejected found for room:", result.roomId);
            navigate(`/`, {
              state: {
                autoRejectCallId: result.roomId
              }
            });
          }
        }
      } catch (e) {
        console.error("Global: Error setting up TelecomPlugin listeners:", e);
      }
    };

    setupTelecomListeners();

    return () => {
      if (ringingListener) ringingListener.remove();
      if (answeredListener) answeredListener.remove();
      if (rejectedListener) rejectedListener.remove();
    };
  }, [navigate]);

  useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) return;

    const setupNativePush = async () => {
      try {
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive === 'granted') {
          await PushNotifications.register();
        }
      } catch (e) {
        console.error("Error setting up native push:", e);
      }
    };

    setupNativePush();

    const regListener = PushNotifications.addListener('registration', async (token) => {
      console.log('Push registration success, token: ' + token.value);
      try {
        await appwriteService.registerFCMToken(user.$id, token.value, Capacitor.getPlatform());
        console.log("FCM Token successfully registered to backend.");
      } catch (err) {
        console.error("Failed to register FCM token with backend:", err);
      }
    });

    const errListener = PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error: ', error);
    });

    return () => {
      regListener.then(l => l.remove());
      errListener.then(l => l.remove());
    };
  }, [user]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const listener = CapApp.addListener('appUrlOpen', async (event) => {
      const url = event.url;
      
      if (url.includes('appwrite-callback-694e577200324c720101://com.scanbell.app/success')) {
        console.log("OAuth successful, forcing session check...");
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.close();
        } catch(e) {}
        try {
          const parsedUrl = new URL(url);
          const userId = parsedUrl.searchParams.get('userId');
          const secret = parsedUrl.searchParams.get('secret');
          
          if (userId && secret) {
            console.log("Found native Appwrite tokens, completing session...");
            await appwriteService.completeOAuthSession(userId, secret);
          }
        } catch(e) {
          console.error("Error parsing native auth URL:", e);
        }

        // Navigate to /dashboard to trigger the AuthCallback sync
        navigate('/dashboard');
      } else if (url.includes('appwrite-callback-694e577200324c720101://com.scanbell.app/failure')) {
        console.log("OAuth failed.");
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.close();
        } catch(e) {}
        setLoading(false);
        navigate('/login');
      } else {
        // Fallback for regular app links
        try {
          const parsedUrl = new URL(url);
          if (parsedUrl.pathname) navigate(parsedUrl.pathname + parsedUrl.search);
        } catch (e) {}
      }
    });
    
    return () => {
      listener.then(l => l.remove());
    };
  }, [navigate, checkAuth, setLoading]);

  useEffect(() => {
    // Specialized check for the OAuth callback route
    if (location.pathname === '/dashboard') {
      console.log("App: Dashboard callback detected. Letting AuthCallback handle it.");
      // We still need to ensure loading is false so AuthCallback can render
      setLoading(false);
      return;
    }

    // For all other routes, verify the session
    console.log(`App: Checking auth for path: ${location.pathname}`);
    checkAuth();
    verifyAppwriteSetup();
  }, [checkAuth, setLoading, location.pathname]);

  // Log state changes for debugging
  useEffect(() => {
    console.log("[App State] User:", user ? user.email : "Logged Out", "| Loading:", loading);
  }, [user, loading]);

  if (loading) {
    return (
      <div className="app-loading">
        <Loader2 className="w-10 h-10 animate-spin text-teal-500" />
      </div>
    );
  }

  const handleLogin = async () => {
    try {
      appwriteService.loginWithGoogle();
    } catch (error) {
      console.error('Google login trigger failed:', error);
      toast.error("Could not start Google login. Please try again.");
    }
  };

  return (
    <div className="app-container">
      <Toaster position="top-right" richColors />
      <Suspense fallback={
        <div className="app-loading flex items-center justify-center min-h-screen">
          <Loader2 className="w-10 h-10 animate-spin text-teal-500" />
        </div>
      }>
        <Routes>
        <Route
          path="/"
          element={user ? <Dashboard user={user} onLogout={logout} /> : <LandingPage onLogin={() => navigate('/login')} />}
        />
        <Route
          path="/dashboard"
          element={
            <AuthCallback onAuth={setUser} />
          }
        />
        <Route path="/call/:userId" element={<VisitorCallPage />} />
        <Route path="/incoming-ring" element={<IncomingRingPage />} />
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/app-auth" element={<AppAuthBridge />} />
      </Routes>
      </Suspense>
    </div>
  );
}

export default App;
